import { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Upload, Receipt, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface ExtractedReceiptData {
  merchant?: string;
  amount?: string;
  currency?: string;
  date?: string;
  category?: string;
  items?: Array<{ name: string; price: string }>;
}

interface ReceiptConverterProps {
  tripId?: string;
}

export function ReceiptConverter({ tripId }: ReceiptConverterProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedReceiptData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'upload' | 'processing' | 'review' | 'saved'>('upload');
  const [category, setCategory] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setStep('upload');
    }
  };

  const handleProcess = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setStep('processing');

    try {
      // Simulate OCR processing - in real app, this would call an OCR API
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Mock extracted data
      const mockData: ExtractedReceiptData = {
        merchant: "Coffee Shop",
        amount: "15.50",
        currency: "USD",
        date: new Date().toISOString().split('T')[0],
        category: "food",
        items: [
          { name: "Cappuccino", price: "5.50" },
          { name: "Croissant", price: "4.00" },
          { name: "Tax", price: "1.00" }
        ]
      };
      
      setExtractedData(mockData);
      setCategory(mockData.category || '');
      setStep('review');
      
      toast({
        title: "Receipt processed!",
        description: "Please review and confirm the extracted information.",
      });
    } catch (error) {
      console.error('Error processing receipt:', error);
      toast({
        title: "Processing failed",
        description: "Failed to extract receipt data. Please try again.",
        variant: "destructive"
      });
      setStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!extractedData || !user) return;

    setIsProcessing(true);
    
    try {
      // Here you would save to the expenses table
      // const { error } = await supabase.from('expenses').insert({...})
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStep('saved');
      toast({
        title: "Receipt saved!",
        description: "Your expense has been added to your trip.",
      });
    } catch (error) {
      console.error('Error saving receipt:', error);
      toast({
        title: "Save failed",
        description: "Failed to save expense. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setExtractedData(null);
    setCategory('');
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-8 text-center">
        <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">Upload Receipt Photo</h3>
        <p className="text-muted-foreground mb-4">
          Take a photo or upload an image of your receipt
        </p>
        
        <div className="flex gap-3 justify-center">
          <Button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Choose File
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2">
            <Camera className="h-4 w-4" />
            Take Photo
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      
      {selectedFile && (
        <div className="space-y-3">
          <div className="bg-secondary/50 rounded-lg p-4">
            <h4 className="font-medium mb-2">Selected File</h4>
            <p className="text-sm text-muted-foreground">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB</p>
          </div>
          <Button onClick={handleProcess} className="w-full">
            Process Receipt
          </Button>
        </div>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-8">
      <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary mb-4" />
      <h3 className="text-lg font-medium mb-2">Processing Receipt</h3>
      <p className="text-muted-foreground">
        Extracting information from your receipt using OCR...
      </p>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-green-600 mb-4">
        <CheckCircle className="h-5 w-5" />
        <span className="font-medium">Receipt processed successfully!</span>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Merchant</Label>
            <Input value={extractedData?.merchant || ''} readOnly />
          </div>
          <div>
            <Label>Date</Label>
            <Input value={extractedData?.date || ''} readOnly />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Amount</Label>
            <Input value={`${extractedData?.amount} ${extractedData?.currency}`} readOnly />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="food">Food & Dining</SelectItem>
                <SelectItem value="transport">Transportation</SelectItem>
                <SelectItem value="accommodation">Accommodation</SelectItem>
                <SelectItem value="entertainment">Entertainment</SelectItem>
                <SelectItem value="shopping">Shopping</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {extractedData?.items && extractedData.items.length > 0 && (
          <div>
            <Label>Items</Label>
            <div className="border rounded-lg p-3 space-y-2">
              {extractedData.items.map((item, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>{item.name}</span>
                  <span>{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex gap-3 pt-4">
        <Button onClick={handleSave} disabled={!category} className="flex-1">
          Save Expense
        </Button>
        <Button variant="outline" onClick={handleReset}>
          Start Over
        </Button>
      </div>
    </div>
  );

  const renderSavedStep = () => (
    <div className="text-center py-8">
      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
      <h3 className="text-lg font-medium mb-2">Expense Saved!</h3>
      <p className="text-muted-foreground mb-4">
        Your receipt has been processed and saved to your trip expenses.
      </p>
      <Button onClick={handleReset}>
        Process Another Receipt
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Photo Receipt Converter
        </CardTitle>
        <CardDescription>
          Convert receipt photos to digital expense records using AI
        </CardDescription>
      </CardHeader>
      <CardContent>
        {step === 'upload' && renderUploadStep()}
        {step === 'processing' && renderProcessingStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'saved' && renderSavedStep()}
        
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Tips for better results:</strong>
              <ul className="mt-1 space-y-1">
                <li>• Ensure good lighting and clear text</li>
                <li>• Keep the receipt flat and avoid shadows</li>
                <li>• Capture the entire receipt in the frame</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}